import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowLeft, BarChart3, ExternalLink, Lock, LockOpen } from "lucide-react";
import type {
  QuestionResults,
  Survey,
} from "@event-planner/shared";
import { PageHeader } from "../../components/PageHeader";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { db } from "../../lib/firebase";
import { useFirestoreDocOnce } from "../../hooks/useFirestoreDocOnce";
import {
  useCloseSurvey,
  useReopenSurvey,
  useDeleteSurvey,
  useSurveyResults,
} from "../../hooks/useSurveyMutations";
import { useSnackbar } from "../../context/SnackbarContext";
import { buildSurveyResultsPowerBILink } from "../../utils/powerbi";
import { PhoneHeaderActions } from "../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../components/phone/OverflowMenu";
import { useIsPhone } from "../../hooks/useIsPhone";
import {
  isSharePointConfigured,
  getSurveyResponsesUrl,
} from "../../utils/sharepoint";

// ── Helper: stat card ──
function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5">{value}</Typography>
    </Paper>
  );
}

// ── Per-question results renderer ──
function QuestionResultsCard({
  question,
  index,
}: {
  question: QuestionResults;
  index: number;
}) {
  const isPhone = useIsPhone();
  const { results, prompt } = question;
  const totalResponses = results.totalResponses;

  return (
    <Paper sx={{ p: isPhone ? 2 : 3 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        Question {index + 1}
      </Typography>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {prompt}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap={isPhone}
        flexWrap={isPhone ? "wrap" : undefined}
        sx={{ mb: 2 }}
      >
        <Chip
          label={
            results.type === "single_choice"
              ? "Multiple Choice"
              : results.type === "rating"
                ? "Rating (1–5)"
                : "Free Text"
          }
          size="small"
          variant="outlined"
        />
        <Chip
          label={`${totalResponses} response${totalResponses === 1 ? "" : "s"}`}
          size="small"
          variant="outlined"
        />
        {results.type === "rating" && totalResponses > 0 && (
          <Chip
            label={`Avg ${results.average.toFixed(1)}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Stack>

      {totalResponses === 0 && (
        <Typography color="text.secondary">No answers yet.</Typography>
      )}

      {results.type === "single_choice" && totalResponses > 0 && (
        <Stack spacing={1.5}>
          {results.options.map((opt) => (
            <Box key={opt.label}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">{opt.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {opt.count} ({opt.pct}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={opt.pct}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          ))}
        </Stack>
      )}

      {results.type === "rating" && totalResponses > 0 && (
        <Stack spacing={1.5}>
          {results.distribution.map((d) => (
            <Box key={d.star}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">
                  {"★".repeat(d.star)}
                  {"☆".repeat(5 - d.star)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {d.count} ({d.pct}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={d.pct}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          ))}
        </Stack>
      )}

      {results.type === "free_text" && totalResponses > 0 && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            Order is shuffled server-side. Responses are anonymous —
            respondent identities are never returned to the admin.
          </Typography>
          <Stack spacing={1}>
            {results.responses.map((text, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">{text}</Typography>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
}

export function SurveyDetailPage() {
  const isPhone = useIsPhone();
  const { surveyId } = useParams<{
    eventId: string;
    surveyId: string;
  }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();

  // ── Survey doc ──
  const docRef = useMemo(
    () => (surveyId ? doc(db, "surveys", surveyId) : null),
    [surveyId],
  );
  const { data: survey, loading, refetch } = useFirestoreDocOnce<Survey>(docRef);

  // ── Aggregate results via callable (ADR-054 §3, ADR-067) ──
  const [refreshToken, setRefreshToken] = useState(0);
  const {
    results,
    loading: resultsLoading,
  } = useSurveyResults(surveyId, refreshToken);

  // ── Mutations ──
  const { close, loading: closing } = useCloseSurvey();
  const { reopen, loading: reopening } = useReopenSurvey();
  const { remove, loading: deleting } = useDeleteSurvey();
  const [showDelete, setShowDelete] = useState(false);

  async function handleClose() {
    if (!surveyId) return;
    try {
      await close(surveyId);
      showSuccess("Survey closed");
      refetch();
      setRefreshToken((n) => n + 1);
    } catch {
      showError("Failed to close survey");
    }
  }

  async function handleReopen() {
    if (!surveyId) return;
    try {
      await reopen(surveyId);
      showSuccess("Survey reopened");
      refetch();
      setRefreshToken((n) => n + 1);
    } catch {
      showError("Failed to reopen survey");
    }
  }

  async function handleDelete() {
    if (!surveyId) return;
    try {
      await remove(surveyId);
      showSuccess("Survey deleted");
      navigate("..", { relative: "path" });
    } catch {
      showError("Failed to delete survey");
    }
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="Survey" />
        <LinearProgress />
      </Box>
    );
  }

  if (!survey) {
    return (
      <Box>
        <PageHeader title="Survey" />
        <Typography>Survey not found.</Typography>
      </Box>
    );
  }

  const firstQuestion = survey.questions[0];
  const totalRespondents = results?.totalRespondents ?? 0;
  const questionCount = survey.questions.length;

  // Power BI deep link uses the first question's type to pick the
  // template page. Parked surface — link only renders if env is configured.
  const powerBILink = buildSurveyResultsPowerBILink({
    surveyType: firstQuestion.type,
    surveyId: survey.id,
  });

  return (
    <Box>
      <PageHeader
        title={firstQuestion.prompt}
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                survey.status === "open" ? (
                  <Button
                    variant="contained"
                    startIcon={<Lock size={16} />}
                    onClick={handleClose}
                    disabled={closing}
                  >
                    Close Survey
                  </Button>
                ) : survey.status === "closed" ? (
                  <Button
                    variant="outlined"
                    startIcon={<LockOpen size={16} />}
                    onClick={handleReopen}
                    disabled={reopening}
                  >
                    Reopen
                  </Button>
                ) : undefined
              }
              more={[
                ...(powerBILink
                  ? [
                      {
                        label: "View in Power BI",
                        icon: <BarChart3 size={16} />,
                        onClick: () => window.open(powerBILink, "_blank", "noopener"),
                      },
                    ]
                  : []),
                ...(isSharePointConfigured()
                  ? [
                      {
                        label: "Open Responses in SharePoint",
                        icon: <ExternalLink size={16} />,
                        onClick: () =>
                          window.open(getSurveyResponsesUrl(), "_blank", "noopener"),
                        testId: "open-sharepoint-survey-button",
                      },
                    ]
                  : []),
                ...(survey.status === "draft"
                  ? [
                      {
                        label: "Delete",
                        onClick: () => setShowDelete(true),
                        destructive: true,
                      } satisfies OverflowAction,
                    ]
                  : []),
                {
                  label: "Back to surveys",
                  icon: <ArrowLeft size={16} />,
                  onClick: () => navigate("..", { relative: "path" }),
                },
              ]}
            />
          ) : (
          <Stack direction="row" spacing={1}>
            {powerBILink && (
              <Button
                variant="outlined"
                startIcon={<BarChart3 size={16} />}
                href={powerBILink}
                target="_blank"
                rel="noopener noreferrer"
              >
                View in Power BI
              </Button>
            )}
            {isSharePointConfigured() && (
              <Button
                variant="outlined"
                startIcon={<ExternalLink size={16} />}
                href={getSurveyResponsesUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-sharepoint-survey-button"
              >
                Open Responses in SharePoint
              </Button>
            )}
            {survey.status === "open" && (
              <Button
                variant="outlined"
                startIcon={<Lock size={16} />}
                onClick={handleClose}
                disabled={closing}
              >
                Close Survey
              </Button>
            )}
            {survey.status === "closed" && (
              <Button
                variant="outlined"
                startIcon={<LockOpen size={16} />}
                onClick={handleReopen}
                disabled={reopening}
              >
                Reopen
              </Button>
            )}
            {survey.status === "draft" && (
              <Button
                color="error"
                variant="outlined"
                onClick={() => setShowDelete(true)}
              >
                Delete
              </Button>
            )}
            <Button
              variant="text"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate("..", { relative: "path" })}
            >
              Back
            </Button>
          </Stack>
          )
        }
      />

      {/* ── Meta ── */}
      <Stack
        direction="row"
        spacing={isPhone ? 1 : 2}
        useFlexGap={isPhone}
        flexWrap={isPhone ? "wrap" : undefined}
        sx={{ mb: 3 }}
      >
        <Chip
          label={survey.status}
          color={survey.status === "open" ? "success" : "default"}
        />
        <Chip
          label={`${questionCount} question${questionCount === 1 ? "" : "s"}`}
          variant="outlined"
        />
        <Chip
          label={survey.sessionId ? "Session-scoped" : "Event-wide"}
          variant="outlined"
        />
        <Chip label="Anonymous" variant="outlined" />
      </Stack>

      {/* ── Stats ── */}
      <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
        <StatCard
          label="Respondents"
          value={resultsLoading ? "..." : totalRespondents}
        />
      </Stack>

      {/* ── Results ── */}
      {resultsLoading && <LinearProgress sx={{ mb: 2 }} />}

      {!resultsLoading && totalRespondents === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No responses yet.</Typography>
        </Paper>
      )}

      {!resultsLoading && totalRespondents > 0 && results && (
        <Stack spacing={3}>
          {results.questions.map((q, i) => (
            <QuestionResultsCard key={q.questionId} question={q} index={i} />
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Delete Survey"
        message={`Delete "${firstQuestion.prompt}"? This also removes all responses. This cannot be undone.`}
        loading={deleting}
      />
    </Box>
  );
}
